/**
 * Map Modes - Factory y Exports
 * 
 * This module exports all map mode strategies and provides a factory to select the correct strategy based on context.
 */

// Export types and interfaces
export type {
	MapModeStrategy,
	Infrastructure,
	PopupData,
	FetchParams,
	MapModeContext,
	DigitalTwinContext,
	BasePopupProps,
	UseMapModeResult,
} from './MapModeStrategy';

// Export strategies
export { default as NormalModeStrategy, normalModeStrategy } from './NormalModeStrategy';
export { default as DigitalTwinModeStrategy, digitalTwinModeStrategy } from './DigitalTwinModeStrategy';
export { default as InflowModeStrategy, inflowModeStrategy } from './InflowModeStrategy';
export { default as MmsModeStrategy, mmsModeStrategy } from './MmsModeStrategy';
export { default as DmaModeStrategy, dmaModeStrategy } from './DmaModeStrategy';
export { default as SewerAnalysisModeStrategy, sewerAnalysisModeStrategy } from './SewerAnalysisModeStrategy';

// Import strategies for factory
import { MapModeStrategy, MapModeContext } from './MapModeStrategy';
import { normalModeStrategy } from './NormalModeStrategy';
import { digitalTwinModeStrategy } from './DigitalTwinModeStrategy';
import { inflowModeStrategy } from './InflowModeStrategy';
import { mmsModeStrategy } from './MmsModeStrategy';
import { dmaModeStrategy } from './DmaModeStrategy';
import { sewerAnalysisModeStrategy } from './SewerAnalysisModeStrategy';

/**
 * Factory to get the correct mode strategy based on context
 * 
 * Priority order:
 * 1. MMS Mode (if activatedMmsMode is true)
 * 2. Digital Twin Mode (if activatedDigitalTwinMode is true)
 * 3. Wet Weather Stress Mode (if activatedInflowMode is true)
 * 4. DMA Mode (if activatedDmaMode is true)
 * 5. Sewer Analysis Mode (if activatedSewerAnalysisMode is true)
 * 6. Normal Mode (default)
 * 
 * @param context - Dashboard context with mode flags
 * @returns The corresponding mode strategy
 */
export function getMapModeStrategy(context: MapModeContext): MapModeStrategy {
	// Priority 1: MMS Mode
	if (context.activatedMmsMode) {
		return mmsModeStrategy;
	}

	// Priority 2: Digital Twin Mode
	if (context.activatedDigitalTwinMode) {
		return digitalTwinModeStrategy;
	}

	// Priority 3: Wet Weather Stress Mode
	if (context.activatedInflowMode) {
		return inflowModeStrategy;
	}

	// Priority 4: DMA Mode
	if (context.activatedDmaMode) {
		return dmaModeStrategy;
	}

	// Priority 5: Sewer Analysis Mode
	if (context.activatedSewerAnalysisMode) {
		return sewerAnalysisModeStrategy;
	}

	// Default: Normal Mode
	return normalModeStrategy;
}

/**
 * Get the name of the active mode based on context
 * 
 * @param context - Dashboard context with mode flags
 * @returns Name of the active mode
 */
export function getActiveModeName(context: MapModeContext): string {
	if (context.activatedMmsMode) {
		return 'mms';
	}
	if (context.activatedDigitalTwinMode) {
		return 'digitalTwin';
	}
	if (context.activatedInflowMode) {
		return 'inflow';
	}
	if (context.activatedDmaMode) {
		return 'dma';
	}
	if (context.activatedSewerAnalysisMode) {
		return 'sewerAnalysis';
	}
	return 'normal';
}

/**
 * Check if a specific mode is active
 * 
 * @param context - Dashboard context with mode flags
 * @param modeName - Name of the mode to check
 * @returns true if the mode is active
 */
export function isModeActive(context: MapModeContext, modeName: string): boolean {
	const activeMode = getActiveModeName(context);
	return activeMode === modeName;
}

/**
 * Map of all available strategies
 * Useful for iteration or direct access by name
 */
export const modeStrategies: Record<string, MapModeStrategy> = {
	normal: normalModeStrategy,
	digitalTwin: digitalTwinModeStrategy,
	inflow: inflowModeStrategy,
	mms: mmsModeStrategy,
	dma: dmaModeStrategy,
	sewerAnalysis: sewerAnalysisModeStrategy,
};

/**
 * List of available mode names
 */
export const availableModes = ['normal', 'digitalTwin', 'inflow', 'mms', 'dma', 'sewerAnalysis'] as const;
export type MapModeName = typeof availableModes[number];
